import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useSectionParallax } from "../hooks/useAnimations";

const events = [
  {
    num: "01",
    title: "Geburtstage",
    desc: "Dein Geburtstag verdient mehr als das Übliche. Exklusive Bahnen, deine Crew und ein Abend, den niemand vergisst.",
    tags: ["Eigene Bahn", "Auf Wunsch Deko", "Getränkepakete"],
    testId: "event-birthday",
  },
  {
    num: "02",
    title: "Firmenfeiern",
    desc: "Teambuilding auf Piratenniveau. Bowling-Turniere, Dart-Duelle und echte Verbindungen zwischen Kollegen.",
    tags: ["Ab 20 Personen", "Firmenpakete", "Außerhalb der Öffnungszeiten möglich"],
    testId: "event-corporate",
  },
  {
    num: "03",
    title: "Gruppenbuchungen",
    desc: "Große Gruppe? Wir machen Platz. Mehrere Bahnen, persönlicher Service und maßgeschneiderte Arrangements.",
    tags: ["Flexible Größen", "Mehrere Bahnen", "Persönliche Beratung"],
    testId: "event-group",
  },
  {
    num: "04",
    title: "Wochenend-Events",
    desc: "Das Wochenende beginnt hier. Freitag und Samstag bis 02:00 Uhr – das Pirate Bowling Center ist dein Hafen.",
    tags: ["Fr & Sa bis 02:00", "Bar durchgehend", "Walk-in willkommen"],
    testId: "event-weekend",
  },
];

export default function Events() {
  const { ref: parallaxRef, y: circleY } = useSectionParallax(60);

  return (
    <section
      ref={parallaxRef}
      id="events"
      className="relative py-28 md:py-36 px-6 md:px-12 lg:px-20 section-alt"
      data-testid="events-section"
    >
      {/* Background texture */}
      <motion.div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ y: circleY }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full border border-gold/28" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-gold/18" />
        <div className="hidden 2xl:block absolute bottom-0 right-0 w-96 h-96 bg-gold/10 blur-[150px] rounded-full" />
      </motion.div>

      {/* Giant background text */}
      <motion.div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden" style={{ y: circleY }}>
        <span className="font-unbounded text-[18vw] font-black text-white/[0.015] select-none whitespace-nowrap">
          EVENTS
        </span>
      </motion.div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <div className="skull-divider mb-6">
            <span className="font-space-grotesk text-xs text-gold tracking-[0.3em] uppercase px-3">Events & Gruppen</span>
          </div>
          <h2 className="font-unbounded text-4xl sm:text-5xl lg:text-6xl font-black text-cream leading-tight">
            Jeder Anlass ein{" "}
            <span className="gold-gradient-text">Abenteuer</span>
          </h2>
        </motion.div>

        {/* Event List – horizontal numbered layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {events.map((ev, i) => (
            <motion.div
              key={ev.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.1 }}
              className="glass border border-white/5 rounded-2xl p-7 md:p-8 group gold-glow-hover cursor-default"
              data-testid={ev.testId}
            >
              {/* Number + Title Row */}
              <div className="flex items-start gap-5 mb-5">
                <span className="font-unbounded text-4xl font-black gold-gradient-text opacity-40 leading-none select-none flex-shrink-0">
                  {ev.num}
                </span>
                <div>
                  <h3 className="font-unbounded text-base sm:text-xl font-bold text-cream group-hover:text-gold transition-colors duration-300 break-words">
                    {ev.title}
                  </h3>
                </div>
              </div>

              {/* Divider */}
              <div className="w-full h-px bg-gradient-to-r from-gold/20 to-transparent mb-5" />

              <p className="font-inter text-cream/80 text-sm leading-relaxed mb-5">
                {ev.desc}
              </p>

              <div className="flex flex-wrap gap-2">
                {ev.tags.map((tag) => (
                  <span key={tag} className="font-space-grotesk text-xs px-3 py-1 rounded-full border border-gold/30 text-cream/80 bg-gold/15">
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-14"
        >
          <a
            href="mailto:info@pirate-bowling.com?subject=Event%20Anfrage"
            className="inline-flex items-center gap-3 btn-gold px-10 py-5 rounded-full text-sm"
            data-testid="events-cta-btn"
          >
            Event anfragen
            <ArrowRight className="w-4 h-4" />
          </a>
          <p className="font-inter text-cream/50 text-xs mt-4">
            Antwort innerhalb von 24 Stunden
          </p>
        </motion.div>
      </div>
    </section>
  );
}
